<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Page Backgrounds</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage page background styles for your application</p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                :disabled="loading"
                                                @click="loadData"
                                        >
                                                <svg class="w-4 h-4" :class="{ 'animate-spin': loading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                @click="showCreateModal = true"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create Background
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Search Card -->
                        <div class="bg-white rounded-xl shadow-sm py-4 px-6 mb-4">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-[12rem]">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search backgrounds..."
                                                                class="w-full py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 py-1 px-3 rounded-full font-medium">
                                                        {{ filteredItems.length }} backgrounds
                                                </span>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="loading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading page backgrounds...</span>
                        </div>

                        <!-- Empty State -->
                        <div v-else-if="paginatedItems.length === 0" class="bg-white rounded-xl shadow-sm py-16 px-4 text-center">
                                <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <p class="text-gray-500 text-base m-0">No page backgrounds found</p>
                                <p class="text-gray-400 text-sm mt-2 mb-0">Create a new background to get started</p>
                        </div>

                        <!-- Card Gallery -->
                        <div v-else>
                                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                                        <div
                                                v-for="item in paginatedItems"
                                                :key="item.id"
                                                class="group relative bg-white rounded-xl shadow-sm overflow-hidden transition-all hover:shadow-md cursor-pointer"
                                                @click="selectedItem = item"
                                        >
                                                <div class="aspect-square flex items-center justify-center p-2">
                                                        <img
                                                                v-if="item.image"
                                                                :src="item.image"
                                                                alt="Background"
                                                                class="w-full h-full object-cover rounded-lg"
                                                                @error="(e) => (e.target as HTMLImageElement).style.display = 'none'"
                                                        >
                                                        <div
                                                                v-else-if="item.color"
                                                                class="w-full h-full rounded-lg border border-gray-200"
                                                                :style="{ backgroundColor: item.color }"
                                                        ></div>
                                                        <svg v-else class="w-12 h-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                </div>
                                                <div class="px-3 py-2 border-t border-gray-100">
                                                        <p class="text-xs text-gray-900 font-medium m-0 truncate">{{ item.name || 'Unnamed' }}</p>
                                                        <p class="text-xs text-gray-500 font-mono m-0 truncate">ID: {{ item.id }}</p>
                                                </div>
                                                <div class="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button
                                                                type="button"
                                                                class="p-1.5 bg-white rounded-lg shadow-sm text-gray-600 border-none cursor-pointer hover:text-orange-7 hover:bg-orange-1"
                                                                title="Rename"
                                                                @click.stop="openRenameModal(item)"
                                                        >
                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                </svg>
                                                        </button>
                                                        <button
                                                                type="button"
                                                                class="p-1.5 bg-white rounded-lg shadow-sm text-gray-600 border-none cursor-pointer hover:text-red-600 hover:bg-red-50"
                                                                title="Delete"
                                                                @click.stop="confirmDelete(item)"
                                                        >
                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                        </button>
                                                </div>
                                        </div>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="flex justify-between items-center py-4 px-6 mt-4 bg-white rounded-xl shadow-sm">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ startRow }} to {{ endRow }} of {{ filteredItems.length }} backgrounds
                                        </div>
                                        <div class="flex gap-2">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage--"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        type="button"
                                                        class="py-2 px-3 border rounded-md text-sm cursor-pointer min-w-[2.5rem]"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300'"
                                                        @click="currentPage = page"
                                                >
                                                        {{ page }}
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage++"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center">
                        <div class="absolute inset-0 bg-black/50" @click="showCreateModal = false"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
                                <div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">Create New Background</h3>
                                        <button
                                                type="button"
                                                class="text-gray-400 bg-transparent border-none cursor-pointer p-1"
                                                @click="showCreateModal = false"
                                        >
                                                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                                        Name <span class="text-red-600">*</span>
                                                </label>
                                                <input
                                                        v-model="newItemName"
                                                        type="text"
                                                        placeholder="Enter background name"
                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
                                                >
                                        </div>
                                </div>
                                <div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                                        <button
                                                type="button"
                                                class="py-2.5 px-4 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm cursor-pointer"
                                                @click="showCreateModal = false"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                :disabled="!newItemName.trim() || isCreating"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="createItem"
                                        >
                                                <svg v-if="isCreating" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                                {{ isCreating ? 'Creating...' : 'Create' }}
                                        </button>
                                </div>
                        </div>
                </div>

                <!-- Preview Modal -->
                <div v-if="selectedItem" class="fixed inset-0 z-50 flex items-center justify-center" @click="selectedItem = null">
                        <div class="absolute inset-0 bg-black/70"></div>
                        <div class="relative max-w-2xl max-h-[80vh] p-8" @click.stop>
                                <button
                                        type="button"
                                        class="absolute top-0 right-0 text-white bg-transparent border-none cursor-pointer p-2 hover:text-gray-300"
                                        @click="selectedItem = null"
                                >
                                        <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                </button>
                                <div class="bg-white rounded-xl p-6 flex items-center justify-center min-w-[20rem] min-h-[20rem] md:min-w-[28rem] md:min-h-[28rem] lg:min-w-[36rem] lg:min-h-[36rem]">
                                        <img
                                                v-if="selectedItem.image"
                                                :src="selectedItem.image"
                                                alt="Background Preview"
                                                class="w-64 h-64 md:w-96 md:h-96 lg:w-[28rem] lg:h-[28rem] object-cover rounded-lg"
                                        >
                                        <div
                                                v-else-if="selectedItem.color"
                                                class="w-64 h-64 md:w-96 md:h-96 lg:w-[28rem] lg:h-[28rem] rounded-lg border border-gray-200"
                                                :style="{ backgroundColor: selectedItem.color }"
                                        ></div>
                                        <div v-else class="w-64 h-64 md:w-96 md:h-96 lg:w-[28rem] lg:h-[28rem] flex items-center justify-center text-gray-400">
                                                No preview available
                                        </div>
                                </div>
                                <div class="text-center text-white text-sm mt-4">
                                        <p class="font-medium m-0">{{ selectedItem.name || 'Unnamed' }}</p>
                                        <p class="font-mono mt-1 m-0">ID: {{ selectedItem.id }}</p>
                                        <p v-if="selectedItem.color" class="mt-1 m-0">Color: {{ selectedItem.color }}</p>
                                </div>
                        </div>
                </div>

                <!-- Rename Modal -->
                <div v-if="renamingItem" class="fixed inset-0 z-50 flex items-center justify-center">
                        <div class="absolute inset-0 bg-black/50" @click="renamingItem = null"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-sm m-4">
                                <div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">Rename Background</h3>
                                        <button
                                                type="button"
                                                class="text-gray-400 bg-transparent border-none cursor-pointer p-1"
                                                @click="renamingItem = null"
                                        >
                                                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                        <input
                                                v-model="renamingName"
                                                type="text"
                                                placeholder="Enter new name"
                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
                                                @keyup.enter="renameItem"
                                        >
                                </div>
                                <div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                                        <button
                                                type="button"
                                                class="py-2.5 px-4 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm cursor-pointer"
                                                @click="renamingItem = null"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                :disabled="!renamingName.trim() || isRenaming"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="renameItem"
                                        >
                                                <svg v-if="isRenaming" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                                {{ isRenaming ? 'Saving...' : 'Save' }}
                                        </button>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import Swal from 'sweetalert2';
import PageBackgroundService from 'services/PageBackgroundService';

export default defineComponent({
        name: 'PageBackgroundsPage',
        setup() {
                const items = ref<any[]>([]);
                const searchQuery = ref('');
                const currentPage = ref(1);
                const pageSize = ref(25);
                const loading = ref(true);
                const showCreateModal = ref(false);
                const newItemName = ref('');
                const isCreating = ref(false);
                const selectedItem = ref<any | null>(null);
                const renamingItem = ref<any | null>(null);
                const renamingName = ref('');
                const isRenaming = ref(false);

                const filteredItems = computed(() => {
                        if (!searchQuery.value.trim()) {
                                return items.value;
                        }
                        const query = searchQuery.value.toLowerCase();
                        return items.value.filter(item =>
                                item.name?.toLowerCase().includes(query) ||
                                item.color?.toLowerCase().includes(query) ||
                                String(item.id).includes(query)
                        );
                });

                const totalRows = computed(() => filteredItems.value.length);
                const totalPages = computed(() => Math.max(1, Math.ceil(totalRows.value / pageSize.value)));
                const startRow = computed(() => totalRows.value === 0 ? 0 : (currentPage.value - 1) * pageSize.value + 1);
                const endRow = computed(() => Math.min(currentPage.value * pageSize.value, totalRows.value));

                const paginatedItems = computed(() => {
                        const start = (currentPage.value - 1) * pageSize.value;
                        return filteredItems.value.slice(start, start + pageSize.value);
                });

                const visiblePages = computed(() => {
                        const pages: number[] = [];
                        const maxVisible = 5;
                        let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
                        const end = Math.min(totalPages.value, start + maxVisible - 1);
                        start = Math.max(1, end - maxVisible + 1);
                        for (let i = start; i <= end; i++) {
                                pages.push(i);
                        }
                        return pages;
                });

                watch([searchQuery], () => {
                        currentPage.value = 1;
                });

                const loadData = async () => {
                        loading.value = true;
                        try {
                                const params = new URLSearchParams({
                                        orderby: 'serialnumber ASC',
                                        limit: '0',
                                });
                                const data = await PageBackgroundService.getAll(params);
                                items.value = data;
                        } catch (error: any) {
                                console.error('Failed to load page backgrounds:', error);
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to load page backgrounds',
                                        icon: 'error',
                                });
                        } finally {
                                loading.value = false;
                        }
                };

                const confirmDelete = async (item: any) => {
                        const result = await Swal.fire({
                                title: 'Delete Background?',
                                text: `Are you sure you want to delete "${item.name || 'this item'}"? This action cannot be undone.`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#dc2626',
                                cancelButtonColor: '#6b7280',
                                confirmButtonText: 'Yes, delete it',
                                cancelButtonText: 'Cancel',
                        });

                        if (result.isConfirmed) {
                                try {
                                        await PageBackgroundService.delete(item.id);
                                        items.value = items.value.filter(i => i.id !== item.id);
                                        Swal.fire({
                                                toast: true,
                                                position: 'top-end',
                                                icon: 'success',
                                                title: 'Background deleted successfully',
                                                showConfirmButton: false,
                                                timer: 3000,
                                        });
                                } catch (error: any) {
                                        Swal.fire({
                                                title: 'Error',
                                                text: error.message || 'Failed to delete background',
                                                icon: 'error',
                                        });
                                }
                        }
                };

                const createItem = async () => {
                        if (!newItemName.value.trim()) return;

                        isCreating.value = true;
                        try {
                                const newItem = await PageBackgroundService.create({ name: newItemName.value.trim() });
                                items.value.unshift(newItem);
                                showCreateModal.value = false;
                                newItemName.value = '';
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: 'Background created successfully',
                                        showConfirmButton: false,
                                        timer: 3000,
                                });
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to create background',
                                        icon: 'error',
                                });
                        } finally {
                                isCreating.value = false;
                        }
                };

                const openRenameModal = (item: any) => {
                        renamingItem.value = item;
                        renamingName.value = item.name || '';
                };

                const renameItem = async () => {
                        if (!renamingItem.value || !renamingName.value.trim()) return;

                        isRenaming.value = true;
                        try {
                                await PageBackgroundService.update(renamingItem.value.id, { name: renamingName.value.trim() });
                                const idx = items.value.findIndex(i => i.id === renamingItem.value.id);
                                if (idx !== -1) {
                                        items.value[idx].name = renamingName.value.trim();
                                }
                                renamingItem.value = null;
                                renamingName.value = '';
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: 'Background renamed successfully',
                                        showConfirmButton: false,
                                        timer: 3000,
                                });
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to rename background',
                                        icon: 'error',
                                });
                        } finally {
                                isRenaming.value = false;
                        }
                };

                onMounted(() => {
                        loadData();
                });

                return {
                        items,
                        searchQuery,
                        currentPage,
                        pageSize,
                        loading,
                        showCreateModal,
                        newItemName,
                        isCreating,
                        selectedItem,
                        filteredItems,
                        totalRows,
                        totalPages,
                        startRow,
                        endRow,
                        paginatedItems,
                        visiblePages,
                        renamingItem,
                        renamingName,
                        isRenaming,
                        confirmDelete,
                        createItem,
                        openRenameModal,
                        renameItem,
                        loadData,
                };
        },
});
</script>
